export type GreenSenseiVariant =
  | "smile"
  | "listening"
  | "thinking"
  | "surprising"
  | "happy"
  | "cry";

export interface GreenSenseiIconProps {
  /**
   * Icon variant/expression
   * smile: 笑顔
   * listening: 聞いている
   * thinking: 考えている
   * surprising: 驚いている
   * happy: 嬉しい
   * cry: 泣いている
   */
  variant?: GreenSenseiVariant;
  /** Size of the icon */
  size?: "S" | "M" | "L";
}

export function GreenSenseiIcon({
  variant = "smile",
  size = "S",
}: GreenSenseiIconProps) {
  // Size mapping
  const sizeMap = {
    S: 24,
    M: 32,
    L: 48,
  };

  const iconSize = sizeMap[size];
  const iconScale = size === "L" ? 2.25 : size === "M" ? 1 : 0.5625;

  // Render different smile variants
  const renderIcon = () => {
    switch (variant) {
      case "smile": // 笑顔
        return (
          <svg
            width={iconSize * iconScale}
            height={iconSize * iconScale}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.9922 0.635742C24.4419 0.636085 31.292 7.48776 31.292 15.9375C31.2915 24.3868 24.4416 31.237 15.9922 31.2373C7.54252 31.2373 0.691881 24.3871 0.691406 15.9375C0.691406 7.48754 7.54223 0.635742 15.9922 0.635742ZM15.9922 2.4375C8.53634 2.4375 2.49219 8.48166 2.49219 15.9375C2.49266 23.3929 8.53664 29.4375 15.9922 29.4375C23.4474 29.4372 29.4917 23.3927 29.4922 15.9375C29.4922 8.48187 23.4477 2.43784 15.9922 2.4375ZM15.9912 3.93652C22.6185 3.93652 27.9911 9.30922 27.9912 15.9365C27.9912 22.5639 22.6186 27.9365 15.9912 27.9365C9.36391 27.9364 3.99121 22.5639 3.99121 15.9365C3.99134 9.3093 9.36399 3.93666 15.9912 3.93652Z"
              fill="#05B45B"
            />
            <path
              d="M8.6416 18.5427C8.6416 17.5444 9.02477 16.7335 9.67676 16.1843C10.3118 15.6495 11.1406 15.4128 11.9697 15.4128C13.8484 15.4128 15.0996 17.0192 15.0996 18.741C15.0996 19.1552 14.7638 19.491 14.3496 19.491C13.9354 19.491 13.5996 19.1552 13.5996 18.741C13.5996 17.6863 12.8675 16.9128 11.9697 16.9128C11.4107 16.9128 10.9505 17.0733 10.6436 17.3318C10.3537 17.5759 10.1416 17.9546 10.1416 18.5427C10.1416 18.9569 9.8058 19.2927 9.3916 19.2927C8.97741 19.2927 8.64163 18.9569 8.6416 18.5427Z"
              fill="#F5F4ED"
            />
            <path
              d="M22.8354 18.5427C22.8354 17.5444 22.4523 16.7335 21.8003 16.1843C21.1652 15.6495 20.3365 15.4128 19.5073 15.4128C17.6286 15.4128 16.3775 17.0192 16.3774 18.741C16.3774 19.1552 16.7132 19.491 17.1274 19.491C17.5417 19.491 17.8774 19.1552 17.8774 18.741C17.8775 17.6863 18.6096 16.9128 19.5073 16.9128C20.0664 16.9128 20.5265 17.0733 20.8335 17.3318C21.1234 17.5759 21.3354 17.9546 21.3354 18.5427C21.3355 18.9569 21.6713 19.2927 22.0854 19.2927C22.4996 19.2927 22.8354 18.9569 22.8354 18.5427Z"
              fill="#F5F4ED"
            />
            <path
              d="M11.2818 12.2699C11.9666 12.1629 12.6932 12.1652 13.6177 12.2425C14.0303 12.2773 14.3368 12.6404 14.3023 13.0531C14.2676 13.4655 13.9052 13.7719 13.4927 13.7377C12.6249 13.665 12.0313 13.6714 11.5132 13.7523C11.0003 13.8324 10.5229 13.9914 9.89211 14.2709C9.51346 14.4386 9.07067 14.2676 8.90285 13.889C8.73508 13.5103 8.90602 13.0676 9.28469 12.8998C9.98121 12.5912 10.5914 12.3777 11.2818 12.2699Z"
              fill="#F5F4ED"
            />
            <path
              d="M20.1904 12.2699C19.5055 12.1629 18.779 12.1652 17.8545 12.2425C17.4419 12.2773 17.1354 12.6404 17.1699 13.0531C17.2046 13.4655 17.567 13.7719 17.9795 13.7377C18.8473 13.665 19.4408 13.6714 19.959 13.7523C20.4718 13.8324 20.9493 13.9914 21.5801 14.2709C21.9587 14.4386 22.4015 14.2676 22.5693 13.889C22.7371 13.5103 22.5661 13.0676 22.1875 12.8998C21.491 12.5912 20.8808 12.3777 20.1904 12.2699Z"
              fill="#F5F4ED"
            />
          </svg>
        );

      case "listening": // 聞いている
        return (
          <svg
            width={iconSize * iconScale}
            height={iconSize * iconScale}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16.1279" cy="16.0364" r="12" fill="#05B45B" />
            <path
              d="M29.6282 16.0366C29.6282 8.58091 23.5838 2.53683 16.1282 2.53662C8.67233 2.53662 2.62817 8.58078 2.62817 16.0366C2.62839 23.4923 8.67246 29.5366 16.1282 29.5366C23.5837 29.5364 29.628 23.4922 29.6282 16.0366ZM31.428 16.0366C31.4278 24.4863 24.5778 31.3362 16.1282 31.3364C7.67835 31.3364 0.827604 24.4864 0.827393 16.0366C0.827393 7.58666 7.67822 0.73584 16.1282 0.73584C24.578 0.736051 31.428 7.58679 31.428 16.0366Z"
              fill="#05B45B"
            />
            <path
              d="M14.3359 20.3148C14.3359 19.9006 14.6717 19.5648 15.0859 19.5648C15.5001 19.5648 15.8359 19.9006 15.8359 20.3148C15.8359 22.0367 14.5848 23.643 12.7061 23.643C11.8769 23.643 11.0481 23.4062 10.4131 22.8715C9.76111 22.3223 9.37793 21.5114 9.37793 20.5131C9.37798 20.0989 9.71375 19.7631 10.1279 19.7631C10.5421 19.7631 10.8779 20.0989 10.8779 20.5131C10.8779 21.1012 11.09 21.4799 11.3799 21.724C11.6868 21.9825 12.147 22.143 12.7061 22.143C13.6038 22.143 14.3359 21.3695 14.3359 20.3148ZM16.3164 20.3148C16.3164 19.9008 16.6524 19.5651 17.0664 19.5648C17.4806 19.5648 17.8164 19.9006 17.8164 20.3148C17.8164 21.3693 18.5487 22.1427 19.4463 22.143C20.0054 22.143 20.4665 21.9825 20.7734 21.724C21.0632 21.4799 21.2744 21.101 21.2744 20.5131C21.2745 20.099 21.6104 19.7633 22.0244 19.7631C22.4386 19.7631 22.7744 20.0989 22.7744 20.5131C22.7744 21.5115 22.3914 22.3223 21.7393 22.8715C21.1042 23.4063 20.2754 23.643 19.4463 23.643C17.5678 23.6427 16.3164 22.0365 16.3164 20.3148ZM12.8076 12.058C13.5009 12.0656 14.2171 12.1883 15.1162 12.4174C15.5175 12.5196 15.7603 12.9282 15.6582 13.3295C15.5559 13.7308 15.1474 13.9736 14.7461 13.8715C13.9021 13.6564 13.3155 13.5638 12.791 13.558C12.2721 13.5523 11.7754 13.6308 11.1074 13.8021C10.7062 13.905 10.2972 13.6633 10.1943 13.2621C10.0915 12.8609 10.3332 12.4519 10.7344 12.349C11.4722 12.1598 12.1091 12.0503 12.8076 12.058ZM19.624 12.058C20.3226 12.0503 20.9594 12.1598 21.6973 12.349C22.0985 12.4519 22.3402 12.8609 22.2373 13.2621C22.1344 13.6633 21.7254 13.905 21.3242 13.8021C20.6562 13.6309 20.1595 13.5523 19.6406 13.558C19.1162 13.5638 18.5295 13.6564 17.6855 13.8715C17.2843 13.9735 16.8757 13.7307 16.7734 13.3295C16.6713 12.9282 16.9141 12.5196 17.3154 12.4174C18.2146 12.1882 18.9308 12.0656 19.624 12.058Z"
              fill="#F5F4ED"
            />
          </svg>
        );

      case "thinking": // 考えている
        return (
          <svg
            width={iconSize * iconScale}
            height={iconSize * iconScale}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16.1646" cy="16.0364" r="12" fill="#05B45B" />
            <path
              d="M1.76465 16.0362C1.76465 13.9867 2.19284 12.0371 2.96465 10.2721M16.1646 1.63623C24.1175 1.63623 30.5646 8.08333 30.5646 16.0362C30.5646 23.9891 24.1175 30.4362 16.1646 30.4362C12.1792 30.4362 8.57192 28.8172 5.96465 26.2009C4.70997 24.9419 3.68687 23.4519 2.96465 21.8003C2.58024 20.9213 2.28107 19.9964 2.07759 19.0362M5.96465 5.87159C5.33402 6.50439 4.76189 7.19554 4.25707 7.93623"
              stroke="#05B45B"
              stroke-width="1.8"
              stroke-linecap="round"
            />
            <path
              d="M10.165 20.0374C10.165 21.624 11.355 22.4172 12.7432 22.4172C14.1314 22.4172 15.123 21.2273 15.123 19.8391"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M22.8589 20.0374C22.8589 21.624 21.669 22.4172 20.2807 22.4172C18.8925 22.4172 17.9009 21.2273 17.9009 19.8391"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M11.8982 14.8823C12.8648 14.2364 13.3798 14.2569 14.5389 14.503"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M18.0991 15.8017C19.4702 16.2787 20.3267 16.3656 22.1009 16.0688"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        );

      case "surprising": // 驚いている
        return (
          <svg
            width={iconSize * iconScale}
            height={iconSize * iconScale}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16.2017" cy="16.0364" r="12" fill="#05B45B" />
            <circle
              cx="16.2013"
              cy="16.0362"
              r="14.4"
              stroke="#05B45B"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-dasharray="3.6 3.6"
            />
            <path
              d="M8.75549 13.3462C9.54606 12.1286 10.1693 11.5347 11.7528 10.6812"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M24.0575 13.3462C23.2669 12.1286 22.6437 11.5347 21.0602 10.6812"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <circle
              cx="12.5982"
              cy="17.5462"
              r="2.77647"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <circle
              cx="20.3375"
              cy="17.5462"
              r="2.77647"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        );

      case "happy": // 嬉しい
        return (
          <svg
            width={iconSize * iconScale}
            height={iconSize * iconScale}
            viewBox="0 0 41 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.9731 1.69241C13.4255 1.48695 13.9588 1.68727 14.1645 2.13968L15.3149 4.67093C15.5204 5.12329 15.3208 5.65654 14.8686 5.86233C14.4162 6.06797 13.882 5.86834 13.6762 5.41604L12.5258 2.88382C12.3203 2.43138 12.5207 1.89805 12.9731 1.69241Z"
              fill="#05B45B"
            />
            <path
              d="M27.2922 1.69241C26.8398 1.48695 26.3064 1.68727 26.1008 2.13968L24.9504 4.67093C24.7448 5.12329 24.9445 5.65654 25.3967 5.86233C25.8491 6.06797 26.3833 5.86834 26.5891 5.41604L27.7395 2.88382C27.9449 2.43138 27.7446 1.89805 27.2922 1.69241Z"
              fill="#05B45B"
            />
            <path
              d="M19.3447 3.66211V0.900391C19.3447 0.403334 19.748 0 20.2451 0C20.7421 0 21.1455 0.403334 21.1455 0.900391V3.66211C21.1454 4.15908 20.7421 4.56152 20.2451 4.56152C19.7481 4.56152 19.3448 4.15908 19.3447 3.66211Z"
              fill="#05B45B"
            />
            <path
              d="M33.1015 5.0463C33.5056 5.33539 33.5988 5.89743 33.3099 6.30172L31.6929 8.56361C31.4039 8.96782 30.8423 9.06155 30.438 8.77282C30.0337 8.48384 29.9395 7.9214 30.2283 7.51704L31.8461 5.25466C32.1352 4.8505 32.6972 4.75732 33.1015 5.0463Z"
              fill="#05B45B"
            />
            <path
              d="M40.2611 17.447C40.2128 16.9525 39.7726 16.5908 39.278 16.6389L36.5107 16.9082C36.0162 16.9564 35.6542 17.3959 35.7021 17.8904C35.7502 18.3851 36.1902 18.7479 36.6847 18.6999L39.4529 18.4301C39.9475 18.3818 40.3092 17.9417 40.2611 17.447Z"
              fill="#05B45B"
            />
            <path
              d="M34.5815 11.5491L36.9732 10.1683C37.4037 9.91975 37.9546 10.0674 38.2032 10.4978C38.4517 10.9283 38.3041 11.4793 37.8736 11.7278L35.4819 13.1087C35.0514 13.3571 34.5013 13.209 34.2528 12.7786C34.0043 12.3482 34.1512 11.7977 34.5815 11.5491Z"
              fill="#05B45B"
            />
            <path
              d="M40.2612 24.155C40.2129 24.6495 39.7727 25.0112 39.2781 24.9631L36.5108 24.6938C36.0162 24.6456 35.6543 24.2061 35.7021 23.7116C35.7503 23.217 36.1902 22.8541 36.6848 22.9021L39.453 23.1719C39.9476 23.2202 40.3093 23.6604 40.2612 24.155Z"
              fill="#05B45B"
            />
            <path
              d="M33.1016 36.5557C33.5057 36.2666 33.5989 35.7046 33.3099 35.3003L31.693 33.0384C31.404 32.6342 30.8424 32.5405 30.4381 32.8292C30.0338 33.1182 29.9396 33.6806 30.2284 34.085L31.8462 36.3474C32.1353 36.7515 32.6973 36.8447 33.1016 36.5557Z"
              fill="#05B45B"
            />
            <path
              d="M35.3695 28.6881L37.7612 30.069C38.1917 30.3175 38.3393 30.8685 38.0908 31.2989C37.8423 31.7294 37.2913 31.877 36.8609 31.6285L34.4691 30.2476C34.0388 29.9991 33.8919 29.4486 34.1404 29.0182C34.3889 28.5878 34.9391 28.4397 35.3695 28.6881Z"
              fill="#05B45B"
            />
            <path
              d="M27.2924 39.9098C26.8399 40.1152 26.3066 39.9149 26.101 39.4625L24.9506 36.9312C24.745 36.4789 24.9447 35.9456 25.3969 35.7398C25.8493 35.5342 26.3835 35.7338 26.5893 36.1861L27.7397 38.7184C27.9451 39.1708 27.7448 39.7041 27.2924 39.9098Z"
              fill="#05B45B"
            />
            <path
              d="M12.9732 39.9098C13.4257 40.1152 13.959 39.9149 14.1646 39.4625L15.315 36.9312C15.5206 36.4789 15.321 35.9456 14.8687 35.7398C14.4163 35.5342 13.8821 35.7338 13.6764 36.1861L12.526 38.7184C12.3205 39.1708 12.5208 39.7041 12.9732 39.9098Z"
              fill="#05B45B"
            />
            <path
              d="M20.9208 37.9401V40.7018C20.9208 41.1988 20.5174 41.6022 20.0204 41.6022C19.5233 41.6022 19.12 41.1988 19.12 40.7018V37.9401C19.1201 37.4431 19.5234 37.0406 20.0204 37.0406C20.5174 37.0406 20.9207 37.4431 20.9208 37.9401Z"
              fill="#05B45B"
            />
            <path
              d="M7.16396 36.5559C6.7598 36.2668 6.66662 35.7047 6.9556 35.3004L8.57253 33.0386C8.8615 32.6343 9.42313 32.5406 9.82746 32.8294C10.2318 33.1183 10.326 33.6808 10.0372 34.0851L8.41938 36.3475C8.13028 36.7517 7.56825 36.8448 7.16396 36.5559Z"
              fill="#05B45B"
            />
            <path
              d="M0.00438478 24.1551C0.0526689 24.6497 0.492817 25.0114 0.987431 24.9633L3.75475 24.6939C4.2493 24.6458 4.61128 24.2063 4.5634 23.7117C4.51528 23.2171 4.0753 22.8543 3.58071 22.9022L0.81254 23.1721C0.317984 23.2204 -0.0437294 23.6605 0.00438478 24.1551Z"
              fill="#05B45B"
            />
            <path
              d="M5.68395 30.053L3.29224 31.4339C2.86177 31.6824 2.31081 31.5348 2.06228 31.1043C1.81375 30.6739 1.96138 30.1229 2.39185 29.8744L4.78356 28.4935C5.214 28.2451 5.76418 28.3932 6.01267 28.8236C6.26117 29.254 6.11429 29.8045 5.68395 30.053Z"
              fill="#05B45B"
            />
            <path
              d="M0.00429426 17.4472C0.0525784 16.9526 0.492727 16.5909 0.987341 16.639L3.75466 16.9084C4.24921 16.9566 4.61119 17.3961 4.56331 17.8906C4.51519 18.3852 4.07521 18.748 3.58062 18.7001L0.81245 18.4302C0.317893 18.382 -0.0438199 17.9418 0.00429426 17.4472Z"
              fill="#05B45B"
            />
            <path
              d="M7.16387 5.04645C6.75971 5.33555 6.66653 5.89758 6.95551 6.30188L8.57244 8.56377C8.86141 8.96798 9.42304 9.06171 9.82737 8.77297C10.2317 8.484 10.3259 7.92155 10.0371 7.51719L8.41929 5.25481C8.13019 4.85066 7.56816 4.75748 7.16387 5.04645Z"
              fill="#05B45B"
            />
            <path
              d="M4.89592 12.9141L2.5042 11.5332C2.07373 11.2847 1.9261 10.7337 2.17463 10.3032C2.42316 9.87278 2.97412 9.72515 3.40459 9.97368L5.79631 11.3545C6.22665 11.6031 6.37352 12.1536 6.12503 12.584C5.87653 13.0144 5.32636 13.1625 4.89592 12.9141Z"
              fill="#05B45B"
            />
            <circle cx="20.3169" cy="20.7625" r="12" fill="#05B45B" />
            <path
              d="M13.5669 23.3685C13.5669 22.3701 13.9501 21.5592 14.6021 21.0101C15.2371 20.4753 16.0659 20.2386 16.895 20.2386C18.7737 20.2386 20.0249 21.8449 20.0249 23.5667C20.0249 23.9809 19.6891 24.3167 19.2749 24.3167C18.8607 24.3167 18.5249 23.9809 18.5249 23.5667C18.5249 22.5121 17.7928 21.7386 16.895 21.7386C16.336 21.7386 15.8758 21.8991 15.5688 22.1575C15.279 22.4017 15.0669 22.7804 15.0669 23.3685C15.0669 23.7827 14.7311 24.1185 14.3169 24.1185C13.9027 24.1185 13.5669 23.7827 13.5669 23.3685Z"
              fill="#F5F4ED"
            />
            <path
              d="M27.7603 23.3685C27.7603 22.3701 27.3771 21.5592 26.7251 21.0101C26.0901 20.4753 25.2613 20.2386 24.4321 20.2386C22.5534 20.2386 21.3023 21.8449 21.3022 23.5667C21.3022 23.9809 21.638 24.3167 22.0522 24.3167C22.4665 24.3167 22.8022 23.9809 22.8022 23.5667C22.8023 22.5121 23.5344 21.7386 24.4321 21.7386C24.9912 21.7386 25.4513 21.8991 25.7583 22.1575C26.0482 22.4017 26.2603 22.7804 26.2603 23.3685C26.2603 23.7827 26.5961 24.1185 27.0103 24.1185C27.4244 24.1185 27.7602 23.7827 27.7603 23.3685Z"
              fill="#F5F4ED"
            />
            <path
              d="M16.3081 14.0716C17.0003 14.0342 17.7229 14.1097 18.6349 14.2799C19.0419 14.356 19.3103 14.7482 19.2343 15.1553C19.1582 15.5622 18.7668 15.8305 18.3598 15.7548C17.5038 15.595 16.9126 15.5415 16.389 15.5698C15.8706 15.5979 15.3796 15.7079 14.7238 15.9223C14.3302 16.051 13.9069 15.8363 13.7781 15.4427C13.6494 15.0491 13.8641 14.6258 14.2577 14.497C14.9818 14.2602 15.6104 14.1093 16.3081 14.0716Z"
              fill="#F5F4ED"
            />
            <path
              d="M24.312 14.0716C23.6198 14.0342 22.8972 14.1097 21.9852 14.2799C21.5782 14.356 21.3099 14.7482 21.3858 15.1553C21.4619 15.5622 21.8534 15.8305 22.2603 15.7548C23.1163 15.595 23.7075 15.5415 24.2312 15.5698C24.7495 15.5979 25.2405 15.7079 25.8963 15.9223C26.2899 16.051 26.7132 15.8363 26.842 15.4427C26.9707 15.0491 26.756 14.6258 26.3624 14.497C25.6383 14.2602 25.0097 14.1093 24.312 14.0716Z"
              fill="#F5F4ED"
            />
          </svg>
        );

      case "cry": // 泣いている
        return (
          <svg
            width={iconSize * iconScale}
            height={iconSize * iconScale}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="15.8745" cy="15.6362" r="12" fill="#05B45B" />
            <path
              d="M9.87402 18.0606C9.87402 19.6472 11.0639 20.4404 12.4522 20.4404C13.8404 20.4404 14.832 19.2505 14.832 17.8623"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M22.5684 18.0606C22.5684 19.6472 21.3784 20.4404 19.9902 20.4404C18.602 20.4404 17.6104 19.2505 17.6104 17.8623"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M8.22863 14.346C9.01921 13.1284 9.64245 12.5345 11.2259 11.681"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M23.5306 14.346C22.7401 13.1284 22.1168 12.5345 20.5333 11.681"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M21.8745 20.1362C22.2745 20.9362 22.3745 21.4696 22.3745 21.6362"
              stroke="#F5F4ED"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <circle
              cx="15.8922"
              cy="15.7638"
              r="14.4"
              stroke="#05B45B"
              stroke-width="1.8"
            />
          </svg>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="bg-primary-light rounded-full overflow-hidden inline-flex items-center justify-center"
      style={{
        width: iconSize,
        height: iconSize,
      }}
    >
      {renderIcon()}
    </div>
  );
}
